import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";
import { VariantProps, cva } from "class-variance-authority";
import {
  Locale,
  addDays,
  addMonths,
  addWeeks,
  addYears,
  endOfMonth,
  endOfWeek,
  endOfYear,
  format,
  getMonth,
  isSameDay,
  isSameMonth,
  isToday,
  setHours,
  setMonth,
  startOfMonth,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subWeeks,
  subYears,
} from "date-fns";
import { enUS } from "date-fns/locale/en-US";
import {
  ReactNode,
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useHotkeys } from "react-hotkeys-hook";
import EventModelV2 from "../../../components/banker/EventModalV2";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import ChevronUpSvg from "../../../icons/ChevronUpSvg";
import ChevronDownSvg from "../../../icons/ChevronDownSvg";
import { LoadingSpinner } from "../../../icons/LoadingSpinner";
import { FocusedCalendar } from "./FocusedCalender";
import { getAuctionEvents } from "services";

const monthEventVariants = cva("size-2 rounded-full", {
  variants: {
    variant: {
      default: "bg-primary",
      blue: "bg-blue-500",
      green: "bg-green-500",
      pink: "bg-pink-500",
      purple: "bg-purple-500",
    },
  },
  defaultVariants: {
    variant: "default",
  },
});

type View = "day" | "week" | "month" | "year";

type ContextType = {
  view: View;
  setView: (view: View) => void;
  date: Date;
  setDate: (date: Date) => void;
  events: CalendarEvent[];
  locale: Locale;
  setEvents: (date: CalendarEvent[]) => void;
  onChangeView?: (view: View) => void;
  onEventClick?: (event: CalendarEvent) => void;
  enableHotkeys?: boolean;
  today: Date;
  modalOpen: boolean;
  setModalOpen: (data: boolean) => void;
  modalData: any;
  setModalData: (data: any) => void;
  selectedDateMonth: any;
  setSelectedDateMonth: (data: any) => void;
};

const Context = createContext<ContextType>({} as ContextType);

export type CalendarEvent = {
  id: string;
  start: Date;
  end: Date;
  title: string;
  color?: VariantProps<typeof monthEventVariants>["variant"];
  borrower_name?: string;
};

type CalendarProps = {
  children: ReactNode;
  defaultDate?: Date;
  view?: View;
  locale?: Locale;
  enableHotkeys?: boolean;
  onChangeView?: (view: View) => void;
  onEventClick?: (event: CalendarEvent) => void;
};

const FullCalendar = ({
  children,
  defaultDate = new Date(),
  locale = enUS,
  enableHotkeys = true,
  view: _defaultMode = "month",
  onEventClick,
  onChangeView,
}: CalendarProps) => {
  const [view, setView] = useState<View>(_defaultMode);
  const [date, setDate] = useState(defaultDate);
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [modalData, setModalData] = useState<any>([]);
  const [selectedDateMonth, setSelectedDateMonth] = useState<{
    selectedDate: number;
    selectedMonth: number;
    selectedYear: number;
  }>({
    selectedDate: 0,
    selectedMonth: -1,
    selectedYear: -1,
  });

  const changeView = (view: View) => {
    setView(view);
    onChangeView?.(view);
  };

  useHotkeys("m", () => changeView("month"), {
    enabled: enableHotkeys,
  });

  useHotkeys("w", () => changeView("week"), {
    enabled: enableHotkeys,
  });

  useHotkeys("y", () => changeView("year"), {
    enabled: enableHotkeys,
  });

  useHotkeys("d", () => changeView("day"), {
    enabled: enableHotkeys,
  });

  const [isLoading, setIsLoading] = useState(false);

  const currentBranch = useSelector((state: any) => {
    return state.bankerAuth.currentBranch;
  });

  const toStartDateISO = (date: Date): string =>
    DateTime.fromJSDate(date, { zone: "Asia/Kolkata" })
      .startOf("day")
      .toUTC()
      .toISO() ?? "";

  const toEndDateISO = (date: Date): string =>
    DateTime.fromJSDate(date, { zone: "Asia/Kolkata" })
      .endOf("day")
      .toUTC()
      .toISO() ?? "";

  const getDateRange = (view: string, date: Date) => {
    switch (view) {
      case "week":
        return {
          start: startOfWeek(date, { weekStartsOn: 1 }),
          end: endOfWeek(date, { weekStartsOn: 1 }),
        };
      case "year":
        return {
          start: startOfYear(date),
          end: endOfYear(date),
        };
      default:
        return {
          start: startOfMonth(date),
          end: endOfMonth(date),
        };
    }
  };

  const getEvents = useCallback(
    async (isActive: () => boolean) => {
      setIsLoading(true);

      try {
        const { start, end } = getDateRange(view, date);

        const response: any = await getAuctionEvents(
          toStartDateISO(start),
          toEndDateISO(end),
        );

        if (!isActive()) return;

        const eventsForCalendar = response?.data?.map((event: any) => {
          return {
            event_name: event?.event_name,
            listing_id: event?.listing_id,
            uuid: event?.uuid,
            short_code: event?.bank?.short_code,
            bank_name: event?.bank?.name,
            city_name: event?.property_dump?.city?.name,
            reserve_price: event?.property_dump?.reserve_price,
            property_type: event?.property_dump?.asset?.name,
            borrower_name: event?.borrower_name,
            id: event?.listing_id,
            start: new Date(event?.start_date_time),
            end: new Date(event?.end_date_time),
            title: event?.event_name,
            color: "blue",
          };
        });

        setEvents(eventsForCalendar);
      } catch (error) {
        if (isActive()) {
          console.error("Error fetching data:", error);
        }
      } finally {
        if (isActive()) {
          setIsLoading(false);
        }
      }
    },
    [view, date, currentBranch],
  );

  useEffect(() => {
    let active = true;

    getEvents(() => active);

    return () => {
      active = false;
    };
  }, [getEvents]);

  return (
    <Context.Provider
      value={{
        view,
        setView,
        date,
        setDate,
        events,
        setEvents,
        locale,
        enableHotkeys,
        onEventClick,
        onChangeView,
        today: new Date(),
        modalOpen: false,
        setModalOpen,
        modalData: [],
        setModalData,
        selectedDateMonth,
        setSelectedDateMonth,
      }}
    >
      {children}
      {modalOpen && (
        <div className="flex justify-center items-center bg-gray-300 bg-opacity-60 absolute bottom-0 w-full -top-3 md:-top-6 right-0 z-50">
          <EventModelV2
            eventForSelectedDate={modalData}
            handleModel={() => setModalOpen(false)}
            selectedDateMonth={selectedDateMonth}
          />
        </div>
      )}
      {isLoading && (
        <div className="flex justify-center items-center bg-gray-300 bg-opacity-60 absolute bottom-0 w-full -top-3 md:-top-6 right-0 z-50">
          <LoadingSpinner />
        </div>
      )}
    </Context.Provider>
  );
};

export const useCalendar = () => useContext(Context);

const FullCalendarViewTrigger = forwardRef<
  HTMLButtonElement,
  React.HTMLAttributes<HTMLButtonElement> & {
    view: View;
  }
>(({ children, view, ...props }, ref) => {
  const { view: currentView, setView, onChangeView } = useCalendar();

  return (
    <Button
      aria-current={currentView === view}
      size="sm"
      variant="ghost"
      ref={ref}
      {...props}
      onClick={() => {
        setView(view);
        onChangeView?.(view);
      }}
    >
      {children}
    </Button>
  );
});
FullCalendarViewTrigger.displayName = "CalendarViewTrigger";

const EventGroup = ({
  events,
  hour,
}: {
  events: CalendarEvent[];
  hour: Date;
}) => {
  const { setModalData, setModalOpen, setSelectedDateMonth, view } =
    useCalendar();
  const hourEnd = new Date(hour);
  hourEnd.setHours(hourEnd.getHours() + 1);
  const overlappingEvents = events.filter(
    (event) => event.start >= hour && event.start < hourEnd,
  );

  return (
    <div
      onClick={() => {
        if (overlappingEvents?.length > 0) {
          setModalOpen(true);
          setModalData(overlappingEvents);
          setSelectedDateMonth({
            selectedDate: hour.getDate(),
            selectedMonth: hour.getMonth() + 1,
            selectedYear: hour.getFullYear(),
            selectedTime: hour.getHours(),
          });
        }
      }}
      className="h-20 border-t hover:cursor-pointer last:border-b flex space-x-1 min-w-0"
    >
      {overlappingEvents?.length === 1 ? (
        <div
          className={cn(
            `relative flex flex-col justify-between font-openSans font-semibold text-web-body-sm leading-4 text-brand-bgBlack bg-brand-lightBlue h-full p-[10px] truncate flex-1 min-w-0`,
          )}
          style={{ height: "100%" }}
        >
          <p className="max-w-[200px] mobile:max-w-[300px] sm:max-w-[500px] md:max-w-[600px] lg:max-w-[600px] mid:max-w-[800px] 2xl:max-w-[1100px] 3xl:max-w-[1400px] truncate">
            {overlappingEvents[0]?.title}
          </p>

          <p className="max-w-[200px] mobile:max-w-[300px] sm:max-w-[500px] md:max-w-[600px] lg:max-w-[600px] mid:max-w-[800px] 2xl:max-w-[1100px] 3xl:max-w-[1400px] truncate">{`${formatTo12Hour(
            overlappingEvents[0]?.start,
          )} - ${formatTo12Hour(overlappingEvents[0]?.end)}`}</p>
        </div>
      ) : overlappingEvents?.length > 1 ? (
        <div
          className={cn(
            `relative flex flex-col justify-center items-center font-openSans font-semibold text-web-body-sm leading-4 text-brand-bgBlack bg-brand-lightBlue h-full p-3 truncate text-wrap text-center flex-1 min-w-0`,
          )}
          style={{ height: "100%" }}
        >
          <p>{overlappingEvents?.length} Auction Events</p>
        </div>
      ) : (
        <></>
      )}
    </div>
  );
};

const formatTo12Hour = (time: Date) => {
  let hours = time.getHours() % 12 || 12; // Convert 0 to 12
  let minutes = String(time.getMinutes()).padStart(2, "0");
  let period = time.getHours() >= 12 ? "PM" : "AM";

  return `${hours}:${minutes} ${period}`;
};

const FullCalendarDayView = () => {
  const { view, events, date } = useCalendar();

  if (view !== "day") return null;

  const hours = [...Array(24)].map((_, i) => {
    return new Date(date.setHours(i, 0, 0, 0));
  });

  return (
    <div className="flex relative pt-2 overflow-y-auto overflow-x-hidden h-full">
      <TimeTable />
      <div className="flex-1 min-w-0">
        {hours.map((hour) => (
          <EventGroup key={hour.toString()} hour={hour} events={events} />
        ))}
      </div>
    </div>
  );
};

const FullCalendarWeekView = () => {
  const { view, date, locale, events } = useCalendar();

  const weekDates = useMemo(() => {
    const start = startOfWeek(date, { weekStartsOn: 0 });
    const weekDates = [];

    for (let i = 0; i < 7; i++) {
      const day = addDays(start, i);
      const hours = [...Array(24)].map((_, i) => setHours(day, i));
      weekDates.push(hours);
    }

    return weekDates;
  }, [date]);

  const headerDays = useMemo(() => {
    const daysOfWeek = [];
    for (let i = 0; i < 7; i++) {
      const result = addDays(startOfWeek(date, { weekStartsOn: 0 }), i);
      daysOfWeek.push(result);
    }
    return daysOfWeek;
  }, [date]);

  if (view !== "week") return null;

  return (
    <div className="flex flex-col min-w-[1200px] lg:min-w-0 absolute lg:relative overflow-x-auto overflow-y-auto lg:overflow-x-hidden h-full">
      <div className="flex sticky top-0 bg-card z-40  mb-3">
        <div className="w-[64px] "></div>
        {headerDays.map((date, i) => (
          <div
            key={date.toString()}
            className={cn(
              `text-center flex-1 uppercase font-openSans font-normal text-brand-textGrey leading-4 md:leading-5 gap-1 py-2 border border-l-0 ${
                i === 0 ? "border-l" : ""
              }  text-web-body-sm md:text-web-body-md flex items-center justify-center`,
              isToday(date) && "bg-brand-orange text-white  font-semibold",
            )}
          >
            <div className="flex   items-center">
              <span className="mr-2">{format(date, "E", { locale })}</span>
              <span className={cn("h-6 grid place-content-center")}>
                {format(date, "d")}
              </span>
            </div>
          </div>
        ))}
      </div>
      <div className="flex  flex-1">
        <div className="">
          <TimeTable />
        </div>
        <div className="grid grid-cols-7 w-full border border-collapse">
          {weekDates.map((hours, i) => {
            return (
              <div
                className={cn(
                  "h-full text-sm text-muted-foreground  border-l first:border-l-0",
                )}
                key={hours[0].toString()}
              >
                {hours.map((hour) => {
                  return (
                    <EventGroup
                      key={hour.toString()}
                      hour={hour}
                      events={events}
                    />
                  );
                })}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const FullCalendarMonthView = () => {
  const {
    date,
    view,
    events,
    locale,
    setModalOpen,
    setModalData,
    setSelectedDateMonth,
  } = useCalendar();

  const monthDates = useMemo(() => getDaysInMonth(date), [date]);
  const weekDays = useMemo(() => generateWeekdays(locale), [locale]);

  if (view !== "month") return null;

  return (
    <div className="h-full flex flex-col min-w-[1200px] lg:min-w-0 absolute lg:relative overflow-x-auto">
      <div className="grid grid-cols-7 gap-px sticky top-0 bg-background border-b">
        {weekDays.map((day, i) => (
          <div
            key={day}
            className={cn(
              "mb-2 text-right text-sm text-muted-foreground pr-2",
              [0, 6].includes(i) && "text-muted-foreground/50",
            )}
          >
            {day}
          </div>
        ))}
      </div>
      <div className="grid overflow-hidden -mt-px flex-1 auto-rows-fr p-px grid-cols-7 gap-px">
        {monthDates.map((_date) => {
          const currentEvents = events.filter((event) =>
            isSameDay(event.start, _date),
          );

          return (
            <div
              className={cn(
                "ring-1 p-2 text-sm text-muted-foreground  ring-border overflow-hidden hover:cursor-pointer",
                !isSameMonth(date, _date) && "text-muted-foreground/50",
              )}
              key={_date.toString()}
              onClick={() => {
                if (currentEvents?.length > 0) {
                  setModalOpen(true);
                  setModalData(currentEvents);
                  setSelectedDateMonth({
                    selectedDate: _date.getDate(),
                    selectedMonth: _date.getMonth() + 1,
                    selectedYear: _date.getFullYear(),
                  });
                }
              }}
            >
              <span
                className={cn(
                  "size-6 grid place-items-center rounded-full  lg:mb-1 sticky top-0",
                  isToday(_date) && "bg-primary text-primary-foreground",
                )}
              >
                {format(_date, "d")}
              </span>

              {currentEvents?.slice(0, 2)?.map((event) => {
                return (
                  <div
                    key={event.id}
                    className="px-1 rounded text-sm flex items-center"
                  >
                    <div className="flex w-full p-1 bg-cyan-600 rounded full text-white flex-wrap my-1 text-xs mx-2 whitespace-nowrap">
                      <span className="flex-1 truncate">
                        Borrower: {event.borrower_name}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const FullCalendarYearView = () => {
  const {
    view,
    date,
    today,
    locale,
    events,
    setModalData,
    setModalOpen,
    setSelectedDateMonth,
  } = useCalendar();

  const months = useMemo(() => {
    if (!view) {
      return [];
    }

    return Array.from({ length: 12 }).map((_, i) => {
      return getDaysInMonth(setMonth(date, i));
    });
  }, [date, view]);
  const monthsName: any = {
    1: "January",
    2: "February",
    3: "March",
    4: "April",
    5: "May",
    6: "June",
    7: "July",
    8: "August",
    9: "September",
    10: "October",
    11: "November",
    12: "December",
  };

  const weekDays = useMemo(() => generateWeekdays(locale), [locale]);

  if (view !== "year") return null;

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-10 overflow-auto h-full scrollbar-hide">
      {months.map((days, i) => (
        <div key={days[0].toString()}>
          <span className="text-xl">
            {i >= 0 && i <= 11 && monthsName[i + 1]}
          </span>

          <div className="grid grid-cols-7 gap-2 my-5">
            {weekDays.map((day) => (
              <div
                key={day}
                className="text-center text-xs text-muted-foreground"
              >
                {day}
              </div>
            ))}
          </div>

          <div className="grid gap-x-2 text-center grid-cols-7 text-xs tabular-nums">
            {days.map((_date) => {
              const currentEvents = events.filter((event) =>
                isSameDay(event.start, _date),
              );
              return (
                <div
                  key={_date.toString()}
                  className={cn(
                    getMonth(_date) !== i && "text-muted-foreground",
                  )}
                >
                  <div
                    onClick={() => {
                      if (currentEvents?.length > 0) {
                        setModalOpen(true);
                        setModalData(currentEvents);
                        setSelectedDateMonth({
                          selectedDate: _date.getDate(),
                          selectedMonth: _date.getMonth() + 1,
                          selectedYear: _date.getFullYear(),
                        });
                      }
                    }}
                    className={cn(
                      `aspect-square grid place-content-center size-full tabular-nums ${
                        currentEvents?.length > 0
                          ? "hover:cursor-pointer bg-brand-orange rounded-full text-white"
                          : ""
                      }`,
                      isSameDay(today, _date) &&
                        getMonth(_date) === i &&
                        "bg-primary text-primary-foreground rounded-full",
                    )}
                  >
                    {format(_date, "d")}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

const FullCalendarNextTrigger = forwardRef<
  HTMLButtonElement,
  React.HTMLAttributes<HTMLButtonElement>
>(({ children, onClick, ...props }, ref) => {
  const { date, setDate, view, enableHotkeys } = useCalendar();

  const normalizeDate = useCallback((target: Date) => {
    const today = new Date();
    return isSameMonth(target, today) ? today : startOfMonth(target);
  }, []);

  const next = useCallback(() => {
    let target = date;

    if (view === "day") {
      target = addDays(date, 1);
    }

    if (view === "week") {
      target = addWeeks(date, 1);
    }

    if (view === "month") {
      target = addMonths(date, 1);
    }

    if (view === "year") {
      target = addYears(date, 1);
    }

    setDate(normalizeDate(target));
  }, [date, normalizeDate, setDate, view]);

  useHotkeys("ArrowRight", () => next(), {
    enabled: enableHotkeys,
  });

  return (
    <>
      {view === "day" || view === "week" ? (
        <></>
      ) : (
        <Button
          size="icon"
          variant="outline"
          ref={ref}
          {...props}
          onClick={(e) => {
            next();
            onClick?.(e);
          }}
        >
          {children}
        </Button>
      )}
    </>
  );
});
FullCalendarNextTrigger.displayName = "FullCalendarNextTrigger";

const FullCalendarPrevTrigger = forwardRef<
  HTMLButtonElement,
  React.HTMLAttributes<HTMLButtonElement>
>(({ children, onClick, ...props }, ref) => {
  const { date, setDate, view, enableHotkeys } = useCalendar();

  useHotkeys("ArrowLeft", () => prev(), {
    enabled: enableHotkeys,
  });

  const normalizeDate = useCallback((target: Date) => {
    const today = new Date();
    return isSameMonth(target, today) ? today : startOfMonth(target);
  }, []);

  const prev = useCallback(() => {
    let target = date;

    if (view === "day") {
      target = subDays(date, 1);
    }

    if (view === "week") {
      target = subWeeks(date, 1);
    }

    if (view === "month") {
      target = subMonths(date, 1);
    }

    if (view === "year") {
      target = subYears(date, 1);
    }

    setDate(normalizeDate(target));
  }, [date, normalizeDate, setDate, view]);

  return (
    <>
      {view === "day" || view === "week" ? (
        <></>
      ) : (
        <Button
          size="icon"
          variant="outline"
          ref={ref}
          {...props}
          onClick={(e) => {
            prev();
            onClick?.(e);
          }}
        >
          {children}
        </Button>
      )}
    </>
  );
});
FullCalendarPrevTrigger.displayName = "FullCalendarPrevTrigger";

const FullCalendarTodayTrigger = forwardRef<
  HTMLButtonElement,
  React.HTMLAttributes<HTMLButtonElement>
>(({ children, onClick, ...props }, ref) => {
  const { setDate, enableHotkeys, today } = useCalendar();

  useHotkeys("t", () => jumpToToday(), {
    enabled: enableHotkeys,
  });

  const jumpToToday = useCallback(() => {
    setDate(today);
  }, [today, setDate]);

  return (
    <Button
      variant="outline"
      ref={ref}
      {...props}
      onClick={(e) => {
        jumpToToday();
        onClick?.(e);
      }}
    >
      {children}
    </Button>
  );
});
FullCalendarTodayTrigger.displayName = "FullCalendarTodayTrigger";

const FullCalendarCurrentDate = () => {
  const { date, view, today, setDate } = useCalendar();
  const [isStartDateOpen, setIsStartDateOpen] = useState(false);

  const handleStartDateSelect = useCallback(
    (date: Date | undefined) => {
      if (date) setDate(date);
      else if (date === undefined) setDate(today);
      setIsStartDateOpen(false);
    },
    [setDate, today],
  );

  return (
    <>
      {view === "day" || view === "week" ? (
        <>
          <Popover onOpenChange={setIsStartDateOpen} open={isStartDateOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className="flex px-0 gap-1 sm:gap-2 items-center w-full border-none text-sm rounded-[8px] font-openSans text-brand-textLightGrey"
              >
                {date ? (
                  <span className="font-openSans  font-semibold text-[20px] leading-7 text-brand-bgBlack ">
                    {new Intl.DateTimeFormat("en-GB", {
                      day: "2-digit",
                      month: "long",
                      year: "numeric",
                    }).format(date)}
                  </span>
                ) : (
                  <span className="font-openSans font-normal text-mobile-body-xsm leading-3 md:text-web-body-md md:leading-4">
                    Start date
                  </span>
                )}
                <div className="px-1 lg:px-2">
                  {isStartDateOpen ? (
                    <ChevronUpSvg width={14} height={14} fill={"#7D7D7D"} />
                  ) : (
                    <ChevronDownSvg width={14} height={14} fill={"#7D7D7D"} />
                  )}
                </div>
              </Button>
            </PopoverTrigger>
            <PopoverContent className="bg-white w-fit">
              <FocusedCalendar
                mode="single"
                selected={date}
                onSelect={handleStartDateSelect}
                initialFocus
              />
            </PopoverContent>
          </Popover>
        </>
      ) : (
        <Button className="w-full" variant="outline">
          <time dateTime={date.toISOString()} className="tabular-nums">
            {format(date, "MMMM yyyy")}
          </time>
        </Button>
      )}
    </>
  );
};

const TimeTable = () => {
  const now = new Date();

  return (
    <div className="pr-2 max-w-full">
      {Array.from(Array(25).keys()).map((hour) => {
        const formattedHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
        const period = hour >= 12 ? "PM" : "AM";

        return (
          <div
            className="text-right relative text-xs text-muted-foreground/50 h-20 last:h-0"
            key={hour}
          >
            {now.getHours() === hour && (
              <div
                className="absolute z-10 left-full translate-x-2 w-[1133px] lg:w-dvw h-[2px] bg-red-500"
                style={{
                  top: `${(now.getMinutes() / 60) * 100}%`,
                }}
              >
                <div className="size-2 rounded-full bg-red-500 absolute left-0 top-1/2 -translate-x-1/2 -translate-y-1/2"></div>
              </div>
            )}
            <p className="top-0 -translate-y-1/2 w-14">
              {formattedHour}:00 {period}
            </p>
          </div>
        );
      })}
    </div>
  );
};

const getDaysInMonth = (date: Date) => {
  const startOfMonthDate = startOfMonth(date);
  const startOfWeekForMonth = startOfWeek(startOfMonthDate, {
    weekStartsOn: 0,
  });

  let currentDate = startOfWeekForMonth;
  const calendar = [];

  while (calendar.length < 42) {
    calendar.push(new Date(currentDate));
    currentDate = addDays(currentDate, 1);
  }

  return calendar;
};

const generateWeekdays = (locale: Locale) => {
  const daysOfWeek = [];
  for (let i = 0; i < 7; i++) {
    const date = addDays(startOfWeek(new Date(), { weekStartsOn: 0 }), i);
    daysOfWeek.push(format(date, "EEEEEE", { locale }));
  }
  return daysOfWeek;
};

export {
  FullCalendar,
  FullCalendarCurrentDate,
  FullCalendarDayView,
  FullCalendarMonthView,
  FullCalendarNextTrigger,
  FullCalendarPrevTrigger,
  FullCalendarTodayTrigger,
  FullCalendarViewTrigger,
  FullCalendarWeekView,
  FullCalendarYearView,
};
